import _ from "underscore";
import { Dialog, IDialogButton } from "../ui/framework/dialog";
import errorFilter from "./error.filter";
import storage, { StorageKeys } from "./storage/storage";
import * as logger from "./logger";
import constants from "../constants";
import { getVersion } from "./native/nativeContainer";
import { isNativeApp } from "./native/nativeHelpers";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import Translator from "./translator";
import { getServerDate } from "./storage/date";
import { UserService } from "./users";
import { cleanupSensitiveString, IExtendedError, cleanupSensitiveData } from "./error";
import DriveApi from "./api";
import { toggle, htmlEscape } from "./vanilla";
import Limiter from "./limiter";
import { updateAppCache } from "./appcache";
import { DriveStore } from "../store/reducers";
import TranslationService from "../services/translation";
import dialogHTML from "./error.dialog.tmpl";
import { uuid } from "geotab-uuid";
import serviceWorker from "./serviceWorker";
import { isNetworkError } from "./errorHandler/error.types";
import { ERROR_MESSAGES } from "./errorHandler/error.messages";
import { IFeatureFlagsService } from "../services/featureFlags";
import { ErrorTracker, IErrorReportData } from "./errorHandler/errorTracker";
import { openExternalUrl } from "./native/OpenExternalUrl";
import { getFeedbackSupportUrl } from "./feedback";
import { getString, isVerboseLogging } from "./logger.internal";
import { IndexedDbStorage } from "./storage/indexedDbStorage";
import IndexedDbMigrationFromRedux from "./storage/indexedDbMigrationFromRedux";

interface ICustomData {
    appCacheStatus?: string;
    apiCall?: string;
    storage?: IAdditionalProperties;
    versionInfo?: IVersionInfo;
    browserInfo?: IBrowserInfo;
    tasks?: string | string[];
    actions?: string | string[];
    additionalErrors?: Object[];
    iamCredentialKeys?: string[];
}

export interface IVersionInfo {
    schemaVersion: number;
    databaseVersion: string;
    sourceCodeVersion: string;
    nativeVersion: string | null;
    serverFlags: number;
}

export interface IBrowserInfo {
    inAppContainer: boolean;
    platform: INativeDevice;
    cacheKeys: string[];
    storageUsedKB: string;
    storageType: string;
    windowSize: string;
    userAgent: string;
    timezoneOffset: number;
    language: string;
    cookieEnabled: boolean;
    online: boolean;
}

export interface IAdditionalProperties {
    storage: Object;
    storageSize: Object;
    entityCounts: IIdDictionary<number>;
    status: Object;
    enabledFeatureFlags: string[];
}

export class ErrorHandler {
    private readonly defaultReportedLogsCount = 200;
    private readonly defaultReportedActionsCount = 50;
    private readonly defaultClearingInterval = 60 * 60 * 1000; // hour
    private readonly defaultReportsLimit = 1000;
    private readonly errorsReportLimiter = new Limiter<null>({
        clearingInterval: this.defaultClearingInterval,
        sizeLimit: this.defaultReportsLimit
    });
    private readonly errorsDialogsLimiter = new Limiter<null>({
        clearingInterval: 5 * 60 * 1000,
        resetAmountOnIntervalPass: false
    });

    private readonly dialogTemplate = _.template(dialogHTML);
    private errorDialog: Dialog | undefined;

    constructor(
        private readonly store: DriveStore,
        private readonly userService: UserService,
        private readonly translationService: TranslationService,
        private readonly featureFlagsService: IFeatureFlagsService,
        private readonly errorTracker?: ErrorTracker
    ) {
    }

    get translator(): Translator {
        return this.translationService.getTranslator();
    }

    async sendToErrorTrackerImpl(err: IExtendedError, tag: string): Promise<boolean> {
        const isAssigningToReadOnlyPropertyError = (errorMessage: string) => errorMessage.includes("Attempted to assign to readonly property.");
        const cleanedErrorMessage = err && typeof err.message === "string" ? cleanupSensitiveString(err.message) : undefined;
        let errorToSend;
        let serverFlags: number = this.store.getState().data.serverFlags;
        let allowExternalLogging = (serverFlags & 1) === 1;

        const errMessage = err?.message ?? "";
        this.errorsReportLimiter.add(errMessage);
        if (!allowExternalLogging || this.errorsReportLimiter.isOverflowed(errMessage)) {
            const stack = isAssigningToReadOnlyPropertyError(errMessage) && err?.stack ? ` Stack: ${ err.stack }` : "";
            logger.error(logger.Source.ERROR, `Won't send the error to the error tracker. ${ cleanedErrorMessage ?? "" }${ stack }`);

            return false;
        }

        let isLocalStorageCapacityError = err.message?.indexOf("size quota") !== -1 ;

        try {
            errorToSend = new Error(cleanedErrorMessage);
            if (err instanceof Error) {
                errorToSend.name = err.name;
                errorToSend.stack = err.stack;
            }
        } catch (ex) {
            // If we failed to modify the error (e.g. readonly), create a new one to ensure sanitization
            const newError = new Error(cleanedErrorMessage);
            if (err instanceof Error) {
                newError.name = err.name;
                newError.stack = err.stack;
                const extendedErr = err;
                const extendedNew = newError as IExtendedError;
                if (extendedErr.apiCall) { extendedNew.apiCall = extendedErr.apiCall; }
                if (extendedErr.additionalErrors) { extendedNew.additionalErrors = extendedErr.additionalErrors; }
            }
            errorToSend = newError;

            const exMessage = ex.message ?? "";
            if (isAssigningToReadOnlyPropertyError(exMessage)) {
                logger.error(logger.Source.ERROR, `Can't edit readonly property. Switched to new Error object. ${ cleanedErrorMessage }${ (err?.stack ? ` Stack: ${ err.stack }` : "") }`);
            } else {
                // Sometimes altering the error message property throws. Log it and it ends up in customData.
                logger.error(logger.Source.ERROR, `Failed to clean error. ${ exMessage } Original error: ${ cleanedErrorMessage }`);
            }
        }

        let customData: ICustomData = {
            appCacheStatus: this.getAppCacheStatus(),
            apiCall: err.apiCall || "none",
            storage: await this.getAdditionalProperties(isLocalStorageCapacityError && isVerboseLogging(), tag),
            versionInfo: this.getAppVersionInfo(),
            browserInfo: await this.getBrowserInfo(),
            tasks: await getString(this.logsFilter, this.getReturnedLogRowCount()),
            actions: await getString(this.actionsFilter, this.defaultReportedActionsCount)
        };

        if (err.additionalErrors) {
            customData.additionalErrors = err.additionalErrors;
        }

        let sanitizedCustomData = cleanupSensitiveData(customData);

        const database = this.store.getState().data.lastDatabase || "unknown";
        const currentUser = this.userService.getCurrentUser();
        const user = currentUser ? { id: currentUser.id, name: currentUser.name } : { id: "unknown", name: "unknown" };
        const appVersion = getVersion();

        const logs: string[] = cleanupSensitiveData(await getString(this.logsFilter, 100));
        const iamLogs: string[] = tag === "IAM" ? cleanupSensitiveData(await getString(this.iamLogsFilter, 100)) : [];

        const reportData: IErrorReportData = {
            database,
            user,
            appVersion,
            tag,
            customData: {
                storage: sanitizedCustomData.storage,
                versionInfo: sanitizedCustomData.versionInfo,
                browserInfo: sanitizedCustomData.browserInfo,
                actions: sanitizedCustomData.actions
            },
            logs: logs.length ? logs : undefined,
            iamLogs: iamLogs.length ? iamLogs : undefined
        };

        this.errorTracker?.reportError(errorToSend, reportData);
        return true;
    }

    handleJSImpl(message: string | object, url: string | null, lineNumber: number | null, column: number | null, error: Error, tag = "javascript") {
        let e = error || { message, url, lineNumber, column };

        if (this.isSDKIntegrationError(error)) {
            this.handleSDKIntegrationError();
            return;
        }

        this.sendToErrorTrackerImpl(e, tag);
        if (typeof (message) === "object" && message instanceof Event) { // Android container only
            let target = message.target ? message.target : message.srcElement;
            if (target && target.hasOwnProperty("src") && target["src"].toLowerCase().indexOf("apiv1") !== -1) {
                // Don't care about network errors outside of our appcache, seeing as this is expected with an offline app
                return;
            } else if (!target || !Object.keys(target).length) {
                // No source for this error, ignore it. Seems to be triggered when turning on "Airplane" mode
                return;
            }
            logger.error(logger.Source.ERROR, `A script hasn't loaded in ${ target && target["src"] ? target["src"] : null }`);
            return;
        }

        // Always log JS errors
        if (typeof (message) === "string") {
            logger.error(logger.Source.ERROR, `${ message }. Url: ${ window.location.href }. Source: ${ url }. Line: ${ lineNumber }. Column: ${ column }`);
        }

        if (this.store.getState().data.debugMode) {
            // Kept for error checking for selenium tests
            this.openDialog(true, message, lineNumber, url, [e]);
        }
    }

    handleApiImpl(errorObject: any, silenceError?: boolean): boolean | undefined {
        if (errorFilter.isIgnored(errorObject) || isNetworkError(errorObject)) {
            return false;
        }

        if (this.isJSNativeError(errorObject)) {
            this.handleJSNativeImpl(errorObject, silenceError);
            return undefined;
        }

        if (this.isClearancesError(errorObject)) {
            this.showClearancesDialog();
            return false;
        }

        if (Array.isArray(errorObject.errors) && errorObject.errors.length > 0) {
            let primaryError = errorObject.errors[0];
            let serverError: IExtendedError = new Error(primaryError.message);
            let additionalErrors: any[] = errorObject.errors.slice(0);
            let tempApiError: Error = new Error("API error");

            serverError.name = primaryError.name;
            if (primaryError.stackTrace) {
                serverError.stack = primaryError.stackTrace;
            }

            if (errorObject && errorObject.data && errorObject.data.info && errorObject.data.info.apiCall) {
                serverError.apiCall = errorObject.data.info.apiCall;
            }

            // Remove primary error (already in serverError) and add a generated error in the current callstack so we can know where it failed client side
            additionalErrors.shift();
            additionalErrors.push({ name: tempApiError.name, message: tempApiError.message, stack: tempApiError.stack });

            serverError.additionalErrors = additionalErrors;
            this.sendToErrorTrackerImpl(serverError, "server");
        } else if (typeof (errorObject) === "string" || errorObject instanceof String) {
            // Some server exception objects are just strings
            this.sendToErrorTrackerImpl(new Error(errorObject.toString()), "server");
        } else {
            this.sendToErrorTrackerImpl(errorObject, "server");
        }

        if (!silenceError && this.store.getState().data.debugMode) {
            // Show dialog to user if error is not being silenced
            this.openDialog(false, errorObject.message, null, window.location.href, errorObject ? [errorObject] : []);
        }

        logger.error(logger.Source.ERROR, errorObject.name);
        return undefined;
    }

    handleJSNativeImpl(e: Error, silenceError = false, tag = "javascript"): void {
        if (this.isSDKIntegrationError(e)) {
            this.handleSDKIntegrationError();
            return;
        }

        if (this.shouldLogToErrorTrackerAndShowDialog(e)) {
            this.sendToErrorTrackerImpl(e, tag);
            if (!silenceError && this.store.getState().data.debugMode) {
                this.openDialog(true, e.toString(), null, window.location.href, [e]);
            }
        }
        logger.error(logger.Source.ERROR, e.toString());
    }

    handleSDKIntegrationError(): void {
        // When navigating away from a page with an iframe pointed at an unbound domain on iOS it's
        // possible to get to a state where the callback message handlers within the functions the SDK
        // integrates become undefined, but the function themselves remain. Causing exceptions that look
        // like "null is not an object (evaluating 'window.webkit.messageHandlers...". Whenever an SDK
        // function is called. From that state just manipulating the page URL will cause the SDK
        // integration to reset and fix the problem. Setting the hash to itself is the least intrusive
        // way to do that.
        // @ts-expect-error - setting the location this way is intentional
        window.location = window.location.hash;
    }

    handleUnhandledRejection(event): void {
        event.preventDefault();

        let error = event.reason;
        if (!error && event.detail && event.detail.reason) {
            error = event.detail.reason;
        }
        if (!(error instanceof Error) && event.reason && event.reason.error) {
            error = event.reason.error;
        }
        if (!error) {
            error = event;
        }

        if (this.isSDKIntegrationError(error)) {
            this.handleSDKIntegrationError();
            return;
        }

        if (typeof error === "string") {
            // If the promise was not rejected with an Error object
            // (i.e. reject(new Error("some error message")) vs reject("some error message")
            // we will be unable to get the stack trace. Assuming we want to still log it, will
            // just create a new Error object here using the message provided so we at least have some data available.
            // This only handles rejections with string, rejections with other values (numbers, undefined) will not be logged (shouldn't really be doing this anyways)
            this.sendToErrorTrackerImpl(new Error(error), "UnhandledPromiseRejection");
            return;
        }

        this.sendToErrorTrackerImpl(error, "UnhandledPromiseRejection");
    }

    shouldLogToErrorTrackerAndShowDialog(e: Error): boolean {
        return !e.message
            || (!e.message.includes(ERROR_MESSAGES.OVERLIMIT) &&
                !e.message.includes(ERROR_MESSAGES.ADDIN_INACCESSIBLE) &&
                !e.message.includes(ERROR_MESSAGES.NOTIFICATION_TEXT_NULL) &&
                !e.message.includes(ERROR_MESSAGES.NOTIFICATION_TEXT_EMPTY));
    }

    async getAdditionalDetails(): Promise<logger.AdditionalDetails> {
        let additionalProperties = await this.getAdditionalProperties();
        return {
            userName: this.store.getState().activeUsers[0],
            database: this.store.getState().data.lastDatabase,
            additionalProps: { ...additionalProperties, appCacheStatus: this.getAppCacheStatus() },
            browserInfo: await this.getBrowserInfo(),
            versionInfo: this.getAppVersionInfo()
        };
    }

    provideFeedback(): void {
        if (constants.FEEDBACK.EXTERNAL_SUPPORT_URL) {
            openExternalUrl(getFeedbackSupportUrl(this.translationService.getLanguage()));
        }

        // The in-app "Send feedback" form has been retired; brands without an
        // EXTERNAL_SUPPORT_URL configured have no feedback entry point in the UI,
        // so there is nothing left to do here.
    }

    getAppVersionInfo(): IVersionInfo {
        return {
            schemaVersion: constants.APP_VERSION,
            databaseVersion: this.store.getState().data.databaseVersion,
            sourceCodeVersion: this.store.getState().data.sourceCodeVersion,
            nativeVersion: getVersion() || "",
            serverFlags: this.store.getState().data.serverFlags
        };
    }

    async getBrowserInfo(): Promise<IBrowserInfo> {
        let storageUsedKB = "0 KB";

        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            storageUsedKB = `${ (await IndexedDbStorage.size()).toFixed(1) } KB`;
        } else {
            storageUsedKB = `${ storage.size().toFixed(1) } KB`;
        }

        return {
            inAppContainer: isNativeApp(),
            platform: window.device || {},
            cacheKeys: window.hasOwnProperty("caches") ? await caches.keys() : [],
            storageType: IndexedDbMigrationFromRedux.isIndexedDbInUse ? "IndexedDB" : "localStorage",
            storageUsedKB,
            windowSize: `${ self.innerWidth }x${ self.innerHeight }`,
            userAgent: navigator.userAgent,
            timezoneOffset: new Date().getTimezoneOffset() / 60,
            language: navigator.language,
            cookieEnabled: navigator.cookieEnabled,
            online: navigator.onLine
        };
    }

    private checkForUpdates() {
        serviceWorker
            .update()
            .catch(() => {
                updateAppCache();
            });
    }

    private showClearancesDialog() {
        let clearancesDialog = new Dialog(
            "clearances-dialog",
            "Ensure you have the correct security clearances to use Drive. Please contact your administrator for more details.",
            this.translator,
            "Improper Permissions",
            [
                {
                    text: "OK",
                    callback: () => {
                        clearancesDialog.remove();
                        dispatcher.trigger(DispatcherEvents.UserRequestLogout);
                    }
                }
            ],
            {
                closable: false,
                showCloseIcon: false,
                isNightMode: this.store.getState().data.nightMode
            }
        );
        void clearancesDialog.open(this.translator);
    }

    private getReturnedLogRowCount(): number | undefined {
        if (this.store.getState().data.debugMode || isVerboseLogging()) {
            return undefined;
        }
        return this.defaultReportedLogsCount;
    }

    private getCurrentUser(): any {
        const key = StorageKeys.USERS_KEY;
        let users;

        users = storage.get(key, true) || {};

        let activeUserNames = this.userService.getActiveUserNames();
        let activeUsersArray: any[] = [];

        activeUserNames.forEach(u => {
            users.hasOwnProperty(u) && activeUsersArray.push(users[u]);
        });

        return activeUsersArray[0] || {};
    }

    private tryJSONStringify(object: any, spacer?: string | number): string {
        try {
            return JSON.stringify(object, null, spacer);
        } catch (e) {
            return object;
        }
    }

    private tryJSONParse(str: any): any {
        try {
            return JSON.parse(str);
        } catch (e) {
            return str;
        }
    }

    private formatErrorObjects(errorsArray: any[] | null): string {
        return errorsArray && errorsArray.length ? errorsArray.map(error => {
            let stringBuilder = "";

            if (error.name || (error.data && error.data.type)) {
                stringBuilder += `${ cleanupSensitiveString(error.name || error.data.type) }: `;
            }

            if (error.message) {
                stringBuilder += cleanupSensitiveString(error.message);
            }

            if (error.name || error.message) {
                stringBuilder += "\r\n";
            }

            if (error.stack || error.stackTrace) {
                stringBuilder += `${ error.stack || error.stackTrace }\r\n\r\n`;
            }

            return stringBuilder;
        }).join("\r\n") : "No automatic error information.";
    }

    private openDialog(isJSError: boolean, errorMessage: any, line: number | null, url: string | null, errorsArray: any[] | null) {
        let message = cleanupSensitiveString(errorMessage && errorMessage.message ? errorMessage.message : (errorMessage || ""));
        let intervalPassed = this.errorsDialogsLimiter.didIntervalPass(message);
        this.errorsDialogsLimiter.add(message);

        if (!intervalPassed) {
            return;
        }

        if (isJSError) {
            logger.error(logger.Source.ERROR, `JS: '${ message }' at line ${ line } in '${ url }'`);
        } else {
            logger.error(logger.Source.ERROR, `SERVER: '${ message }'`);
        }

        let stackTrace = this.formatErrorObjects(errorsArray);
        let buttons: IDialogButton[][] = [[]];

        buttons[0].push({
            id: "report-dialog-toggleDetailsButton",
            icon: "drive-plus-circled",
            text: "Details",
            className: "button_secondary",
            callback: (dialog: Dialog) => {
                let errorDialogContainer = document.getElementById("report-dialog-errorContainer");
                errorDialogContainer && toggle(errorDialogContainer);
                dialog.resize();
            }
        });
        buttons[0].push({
            id: "report-dialog-reportButton",
            text: "Send report",
            callback: () => {
                let comment = (<HTMLInputElement>document.querySelector("#report-dialog-additionalInfo")).value;
                let closeDialog = () => {
                    if (this.errorDialog) {
                        this.errorDialog.remove();
                        delete this.errorDialog;
                    }
                };

                this.checkForUpdates();

                this.sendReport({
                    message,
                    url,
                    line,
                    comment,
                    stackTrace
                }, closeDialog, closeDialog);
            }
        });

        this.errorDialog = new Dialog(
            "error",
            this.dialogTemplate({
                isJSError,
                message,
                line,
                url: url && htmlEscape(url),
                errors: stackTrace
            }),
            this.translator,
            "Error",
            buttons,
            {
                isNightMode: this.store.getState().data.nightMode
            }
        );

        void this.errorDialog
            .open(this.translator)
            .then(dialog => {
                this.errorDialog = dialog;
            });
    }

    private async getAdditionalProperties(addStorageSizeDetails = false, tag: string | null = null): Promise<IAdditionalProperties> {
        const storageProps = [
            "@appVersion",
            "@currentVehicle",
            "@active",
            "@driver"
        ];
        let result: IAdditionalProperties = {
            storage: {},
            storageSize: {},
            entityCounts: {},
            status: {},
            enabledFeatureFlags: []
        };

        result.storage[StorageKeys.USERS_KEY] = this.getUsers();
        result.storage["@lastDatabase"] = this.store.getState().data.lastDatabase;
        result.storage["@lastServer"] = this.store.getState().data.lastServer;
        result.storage["@alertsAvailability"] = this.store.getState().data.alertsAvailability;

        tag === "IAM" && (result.storage["@storeIamUserKeys"] = Object.keys(this.store.getState().iamCredentials));

        await Promise.all(
            storageProps.map(propertyName => {
                // The storageProps are non-"@store" keys so don't need IndexedDb lookups
                let storedProp = storage.get(propertyName, false);
                storedProp = this.tryJSONParse(storedProp);
                if (storedProp) {
                    result.storage[propertyName] = storedProp;
                }
            })
        );

        // And storage size in KB
        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            result.storageSize = await IndexedDbStorage.getSizeDistribution() as {};
            result.entityCounts = await IndexedDbStorage.getEntityCounts();
        } else {
            result.storageSize = storage.getSizeDistribution() as {};
            result.entityCounts = storage.getEntityCounts();
        }
        result.enabledFeatureFlags = this.featureFlagsService.getAllEnabledFeatureFlagCsv().split(",");

        // Add top sized keys from localStorage if LocalStorageFull error occurs and verbose logging is enabled
        if (addStorageSizeDetails) {
            const topItems = Object.keys(result.storageSize)
                .map(key => ({
                    key,
                    value: result.storageSize[key]
                }))
                .sort((item1, item2) => item2.value - item1.value) //sort descending
                .slice(0, 3); // taking top 3

            if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
                await Promise.all(
                    topItems.map(async item => {
                        result.storage[item.key] = await IndexedDbStorage.retrieveData(item.key);
                    })
                );
            } else {
                topItems.map(item => {
                    result.storage[item.key] = storage.get(item.key, false, undefined, true)?.slice(0, 500);
                });
            }
        }

        // Add the application "status" in memory
        if (typeof (window.driveApp) !== "undefined") {
            result.status = JSON.parse(JSON.stringify(window.driveApp.store.getState().mobile || {}));
        }

        return result;
    }

    private getUsers(): IIdDictionary<IUser> {
        let users = this.userService.getUsersObject();
        let usersSlim = {};
        for (const username in users) {
            if (users.hasOwnProperty(username)) {
                const user = users[username];
                usersSlim[username] = {
                    id: user.id,
                    name: user.name,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    timeZoneId: user.timeZoneId,
                    hosRuleSet: user.hosRuleSet,
                    licenseProvince: user.licenseProvince,
                    licenseNumber: user.licenseNumber,
                    authorityName: user.authorityName,
                    authorityAddress: user.authorityAddress,
                    companyName: user.companyName,
                    companyAddress: user.companyAddress,
                    carrierNumber: user.carrierNumber,
                    userAuthenticationType: user.userAuthenticationType
                };
            }
        }
        return usersSlim;
    }

    private getAppCacheStatus(): string {
        if (!window.applicationCache) {
            return "NO APP CACHE";
        }
        // Source: http://www.html5rocks.com/en/tutorials/appcache/beginner/
        let appCache = window.applicationCache;
        switch (appCache.status) {
            case appCache.UNCACHED: // UNCACHED == 0
                return "UNCACHED";
            case appCache.IDLE: // IDLE == 1
                return "IDLE";
            case appCache.CHECKING: // CHECKING == 2
                return "CHECKING";
            case appCache.DOWNLOADING: // DOWNLOADING == 3
                return "DOWNLOADING";
            case appCache.UPDATEREADY:  // UPDATEREADY == 4
                return "UPDATEREADY";
            case appCache.OBSOLETE: // OBSOLETE == 5
                return "OBSOLETE";
            default:
                return "UNKNOWN CACHE STATUS";
        }
    }

    private isJSNativeError(errorObject: any): boolean {
        return typeof (errorObject) === "object" && (errorObject instanceof Error);
    }

    private isClearancesError(errorObject: any): boolean {
        return errorObject && errorObject.message && errorObject.message.indexOf("does not have security clearance for the requested operation") > -1;
    }

    private isSDKIntegrationError(errorObject: any): boolean {
        return errorObject && errorObject.message && errorObject.message.toString().startsWith("null is not an object (evaluating 'window.webkit.messageHandlers");
    }

    private logsFilter(this: void, log: logger.ILog): boolean {
        // Skip geolocation, user action sources and event types when submitting error logs
        if (log.source === logger.Source[logger.Source.GEO]
            || log.source === logger.Source[logger.Source.ROUTER]
            || log.source === logger.Source[logger.Source.USER_EVENT]
            || log.type === logger.LogType[logger.LogType.EVENT]) {
            return false;
        }
        return true;
    }

    private iamLogsFilter(this: void, log: logger.ILog): boolean {
        // Only show IAM logs
        return log.source === logger.Source[logger.Source.IAM];
    }

    private actionsFilter(this: void, log: logger.ILog): boolean {
        // Only show user action sources
        if (log.source === logger.Source[logger.Source.ROUTER]
            || log.source === logger.Source[logger.Source.USER_EVENT]) {
            return true;
        }
        return false;
    }

    private async sendReport(errorReport: any, successCallback?: () => void, errorCallback?: () => void): Promise<void> {
        let { userName, browserInfo, versionInfo, additionalProps } = await this.getAdditionalDetails();
        let exceptionName;
        let message;
        let url = errorReport.url || window.location.href;
        let lineNumber = errorReport.line || -1;
        let comment;

        if (typeof (errorReport) === "string") {
            exceptionName = `DriveUserReport: ${ userName || uuid() }`;
            message = "Drive User Reported Problem";
            comment = errorReport;
        } else {
            exceptionName = errorReport.message;
            message = errorReport.message;
            comment = errorReport.comment;
        }

        let currentUser = this.getCurrentUser();
        if (currentUser && currentUser.name) {
            new DriveApi(this.userService, this.store, currentUser.name).call(
                "AddClientError",
                cleanupSensitiveData({
                    error: {
                        exceptionName,
                        message,
                        url,
                        lineNumber,
                        stackTrace: errorReport.stackTrace || "",
                        comment,
                        timeStamp: getServerDate().toISOString(),
                        browserProperties: this.tryJSONStringify(browserInfo, "\t"),
                        otherDetails: this.tryJSONStringify({ ...additionalProps, ...versionInfo }, "\t"),
                        tasks: await getString(undefined, this.getReturnedLogRowCount())
                    }
                }),
                () => {
                    successCallback && successCallback();
                },
                () => {
                    errorCallback && errorCallback();
                }
            );
        }
    }
}
